import * as actions from './slice'

/**
 * Event-bus definitions for What's new (PENG-3809, backend discovery
 * PENG-3662: Elastic listing + lazy body).
 *
 * The event names below are the contract we expect from the backend and do
 * not exist yet. Until they do, `useAnnouncements` drives the same actions
 * from `components/whats-new/mock-announcements.js`, so the reducer/selector
 * path exercised today is already the real one (`WHATS_NEW_API_ENABLED` in
 * `components/whats-new/constants.js` flips it).
 *
 * TODO(API) — everything the backend still owes this feature:
 * - `announcementsList { from, size }` must return `{ items, total }`. `total`
 *   drives "Load more"; the client does not need `from` echoed back — a load
 *   more is recognised by the request it made (`setLoadingMore`). Each item is
 *   `{ id, publishedAt, title, summary, image, featureId, audience }`
 *   (Icaro's draft model, its `thumbnail` carried as the Ark uploader's
 *   `image: { imgUrl, altText, imgixCropParams, ... }`, as on content) plus
 *   what the design needs and the discovery lacks: `type` (feature |
 *   improvement | fix | announcement — the UI falls back to announcement),
 *   `featureName` (feature filter + card label; the filter groups by the exact
 *   string), `planLabel` (un-gated upsell notes, e.g. "Available on
 *   Enterprise"). `id` is the Ark's title slug and never changes once made —
 *   it is the public, shareable /whats-new/:id. Empty optionals come as
 *   `null`. Field-by-field notes live in
 *   components/whats-new/mock-announcements.js.
 * - `announcementRetrieve { id }` returns the same note plus `body` (HTML,
 *   sanitised client-side), `cta` (`{ label, to | href, note? }`) and optional
 *   `media` (`{ type: 'video', url, poster?, caption?, captionsUrl? }` — a .vtt
 *   track; the video and captions hosts must answer CORS). An unknown or
 *   unpublished id must come back as an error, not an empty note, and the
 *   error has to be recognisable as "not found": the slice reads a
 *   `statusCode` or `status` of 404 (or the literal `'not-found'` the mock
 *   sends) — agree the real shape with the backend. Any other error renders
 *   "Couldn't load this update" with a retry.
 * - Tenant features and audience are resolved server-side from the Tenant
 *   object; the client sends nothing but paging, and renders whatever it is
 *   given. A `featureId` the tenant lacks hides the note outright (never tease).
 *   `featureId` is a Tenant.features key; its value is a boolean (mindsetAi,
 *   msb) or `{ enabled }` (skillBite, playlist, skillsPractice), and an absent
 *   key defaults per feature (contentHub absent = on) — resolve it the way
 *   hooks/useProducts.js does. Only published notes are listed or retrieved.
 * - Authoring is the Ark's (manage-content, PENG-3809): `adminAnnouncementList`
 *   plus Get / Create / Update / Publish / Unpublish / Delete. Publishing sets
 *   `publishedAt`.
 * - Newest first. The client re-derives "newest" with a max over
 *   `publishedAt`, but the order of the list is the server's.
 * - Telemetry (below) — three events nobody handles yet. `useTrackWhatsNewEvent`
 *   holds every trigger back until WHATS_NEW_API_ENABLED, so nothing is fired
 *   into the void. The house analytics shape is one sink with an `eventType`
 *   discriminator and a `data` bag (`homeEnterpriseEvent`, `notificationEvent`);
 *   if the backend prefers that, only these three definitions and the hook
 *   change. `source` is a WHATS_NEW_ENTRY_SOURCE value read from `?from=`:
 *   `whatsNewEntryClicked` fires on the first What's new page of a visit,
 *   list or note, but not for `list` / `detail` (moves inside What's new);
 *   `whatsNewItemViewed` carries the source of every note view.
 */

// No `onStart` here, unlike the sibling slices: one event serves both the
// first page and "Load more", and only the caller knows which —
// `useAnnouncements` dispatches `setLoading` or `setLoadingMore` before
// triggering, and `setAnnouncements` reads `loadingMore` to decide whether the
// payload replaces the list or extends it.
export const listAnnouncements = {
  eventName: 'announcementsList',
  onSuccess: {
    action: actions.setAnnouncements,
    redux: true
  },
  onError: {
    action: actions.setError,
    redux: true
  }
}

export const retrieveAnnouncement = {
  eventName: 'announcementRetrieve',
  onSuccess: {
    action: actions.setCurrent,
    redux: true
  },
  onError: {
    action: actions.setCurrentError,
    redux: true
  }
}

// Fire-and-forget telemetry: no state to update, so no handlers.
export const entryClicked = { eventName: 'whatsNewEntryClicked' }

export const itemViewed = { eventName: 'whatsNewItemViewed' }

export const announcementFeedback = { eventName: 'announcementFeedback' }
